/**
 * MobileFeedbackList — Feedback Centre (Complaints & Compliments) mobile
 * card list ( <lg ). Batch 6.1 — pure presentation swap for the desktop
 * `<table data-testid="feedback-table">`. All record editing, archiving,
 * restoring and admin hard-delete continue to run through the SAME
 * handlers the desktop table uses; whole-card tap opens the existing
 * `FeedbackDetailDrawer`. Zero new APIs, statuses, fields, permissions
 * or workflow logic.
 */
import React from "react";
import {
  Heart, MessageSquareWarning, AlertTriangle, ChevronRight, Pencil,
  Archive, ArchiveRestore, Trash2,
} from "lucide-react";

// KIND_META / STATUS_META / PRIORITY_META are re-declared minimally here so
// this component does not import from the page file. Values MUST match
// the desktop table exactly.
const KIND_META = {
  compliment: { label: "Compliment", Icon: Heart, cls: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800" },
  complaint:  { label: "Complaint",  Icon: MessageSquareWarning, cls: "bg-orange-50 text-orange-800 border-orange-200 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-800" },
};
const STATUS_META = {
  draft:       { label: "Draft",        cls: "bg-slate-100 text-slate-700 border-slate-300" },
  open:        { label: "Open",         cls: "bg-red-50 text-red-700 border-red-300" },
  in_progress: { label: "In Progress",  cls: "bg-amber-50 text-amber-800 border-amber-300" },
  closed:      { label: "Closed",       cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  resolved:    { label: "Resolved",     cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
};
const PRIORITY_META = {
  High:   "bg-red-50 text-red-700 border-red-200",
  Medium: "bg-amber-50 text-amber-800 border-amber-200",
  Low:    "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const daysBetween = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};

export default function MobileFeedbackList({
  loading, rows, isAdmin, onOpenDetail, onEdit, onArchive, onRestore, onHardDelete,
}) {
  return (
    <div data-testid="mobile-feedback-list" className="space-y-2">
      {loading ? (
        <div className="text-sm text-slate-500 p-4" data-testid="mobile-feedback-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div
          className="rounded-lg border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 text-center"
          data-testid="mobile-feedback-empty"
        >
          <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">No feedback records match your filter.</div>
        </div>
      ) : (
        rows.map((r, i) => {
          const kind = KIND_META[r.kind] || KIND_META.complaint;
          const KindIcon = kind.Icon;
          const status = STATUS_META[r.status] || STATUS_META.draft;
          const days = daysBetween(r.received_date);
          const stale = r.kind === "complaint" && r.status !== "closed" && r.status !== "resolved" && days != null && days > 28;
          return (
            <div
              key={r.id}
              data-testid={`feedback-row-${r.id}`}
              className={`rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 ${r.archived ? "opacity-60" : ""}`}
            >
              <button
                type="button"
                onClick={() => onOpenDetail(r)}
                data-testid={`mobile-feedback-card-${r.id}`}
                className="w-full text-left p-3 min-h-[44px] active:bg-slate-50 dark:active:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-500 rounded-t-lg"
              >
                {/* Row 1 — Kind chip + received date + chevron */}
                <div className="flex items-center gap-2 mb-1">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full border ${kind.cls}`} data-testid={`mobile-feedback-kind-${r.id}`}>
                    <KindIcon className="w-3 h-3" /> {kind.label}
                  </span>
                  <span className="text-[10px] tabular-nums text-slate-500 dark:text-slate-400" data-testid={`mobile-feedback-received-${r.id}`}>
                    {r.received_date || "—"}
                  </span>
                  <span className="ml-auto"><ChevronRight className="w-4 h-4 text-slate-400" /></span>
                </div>
                {/* Row 2 — Subject (bold) */}
                <div className="font-semibold text-slate-900 dark:text-slate-100 truncate" data-testid={`mobile-feedback-subject-${r.id}`}>
                  {r.subject || <span className="text-slate-300 font-normal">Untitled</span>}
                </div>
                {/* Row 3 — From */}
                <div className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 truncate">
                  <span className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold mr-1">From</span>
                  <span data-testid={`mobile-feedback-from-${r.id}`}>{r.source || "—"}</span>
                </div>
                {/* Row 4 — Service user */}
                <div className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 truncate">
                  <span className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold mr-1">Service user</span>
                  <span data-testid={`mobile-feedback-client-${r.id}`}>{r.service_user || "—"}</span>
                </div>
                {/* Row 5 — Priority + Assigned */}
                <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                  {r.priority ? (
                    <span className={`inline-flex px-2 py-0.5 text-[10px] font-medium rounded-full border ${PRIORITY_META[r.priority] || "bg-slate-50 text-slate-500 border-slate-200"}`} data-testid={`mobile-feedback-priority-${r.id}`}>
                      {r.priority}
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-widest text-slate-400">No priority</span>
                  )}
                  {r.assigned_to && (
                    <span className="text-[11px] text-slate-600 dark:text-slate-400 truncate" data-testid={`mobile-feedback-assigned-${r.id}`}>
                      · {r.assigned_to}
                    </span>
                  )}
                </div>
                {/* Row 6 — Status + stale + archived */}
                <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                  <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full border ${status.cls}`} data-testid={`mobile-feedback-status-${r.id}`}>
                    {status.label}
                  </span>
                  {stale && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-red-100 text-red-800 border border-red-300" title={`Open for ${days} days`} data-testid={`stale-${r.id}`}>
                      <AlertTriangle className="w-2.5 h-2.5" /> {days}d
                    </span>
                  )}
                  {r.archived && <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Archived</span>}
                </div>
              </button>
              {/* Manager actions row — visible only if the caller passes handlers.
                  Care workers do not receive these handlers, so no action row renders. */}
              {onEdit && (
                <div
                  className="border-t border-slate-100 dark:border-slate-800 px-2 py-1 flex items-center gap-1"
                  data-testid={`mobile-feedback-actions-${r.id}`}
                >
                  <button
                    type="button"
                    onClick={() => onEdit(r)}
                    data-testid={`btn-edit-${r.id}`}
                    className="min-h-[44px] flex-1 inline-flex items-center justify-center gap-1 rounded-md text-slate-700 dark:text-slate-200 text-xs font-medium active:bg-slate-100 dark:active:bg-slate-800"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  {!r.archived ? (
                    <button
                      type="button"
                      onClick={() => onArchive(r)}
                      data-testid={`btn-archive-${r.id}`}
                      className="min-h-[44px] flex-1 inline-flex items-center justify-center gap-1 rounded-md text-amber-700 dark:text-amber-400 text-xs font-medium active:bg-amber-50 dark:active:bg-amber-950/40"
                    >
                      <Archive className="w-3.5 h-3.5" /> Archive
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onRestore(r)}
                      data-testid={`btn-restore-${r.id}`}
                      className="min-h-[44px] flex-1 inline-flex items-center justify-center gap-1 rounded-md text-emerald-700 dark:text-emerald-400 text-xs font-medium active:bg-emerald-50 dark:active:bg-emerald-950/40"
                    >
                      <ArchiveRestore className="w-3.5 h-3.5" /> Restore
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => onHardDelete(r)}
                      data-testid={`btn-hard-delete-${r.id}`}
                      className="min-h-[44px] w-11 inline-flex items-center justify-center rounded-md text-red-600 active:bg-red-50 dark:active:bg-red-950/40"
                      aria-label="Permanently delete (admin only)"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
